import {
	DateString,
	Highlight,
	HighlightedText,
	ListItem,
	PipeSeparator,
	Typography
} from "client/Components";
import { ReactElement } from "react";
import styles from "./EventListItem.module.less";
import { HubThumbnail } from "../../HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { useGlobalSearchItemActions } from "client/Features/Layout/TitleBar/GlobalSearch/GlobalSearchItem/useGlobalSearchItemActions";

export type EventHighlightableFields =
	| "title";

export type EventListItemProps = {
	title: string;
	media: HubItemMediaPreviewViewModel;
	startDate: Date | string;
	categoryName: string | nil;
	authorName: string | nil;
	trackingId: string;
	highlights?: Record<EventHighlightableFields, Highlight | undefined>;
	timezone: TimeZone;
	onClick?: () => void;
	onRemove?: () => void;
	itemIndex?: number;
	tabIndex?: number;
	onExecute?: () => void;
};

export const EventListItem = (props: EventListItemProps): ReactElement => {
	const {
		title,
		media,
		startDate,
		categoryName,
		authorName,
		trackingId,
		timezone,
		highlights,
		onClick,
		onRemove,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex: itemIndex ?? -1,
		onExecute: onExecute ?? (() => {/** Nope */})
	});

	return (
		<ListItem
			trackingId={trackingId}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<div className={styles.thumbnailWrapper}>
						<HubThumbnail
							title={title}
							media={media}
							hubItemKind={HubItemKind.Event}
							mediaPlaceholderSize="extra-small"
						/>
					</div>
				}
				onClick={onClick}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						<HighlightedText
							text={title}
							highlight={highlights?.title}
						/>
					</Typography>
				</ListItem.Title>
				<ListItem.SubTitle>
					<span
						{...setTrackingId("timestamp")}
					>
						<DateString
							value={startDate}
							format="DMYT"
							timezone={timezone}
						/>
					</span>
					{isNotNilOrWhiteSpace(authorName) && (
						<>
							<PipeSeparator />
							<span {...setTrackingId("authorName")}>
								{`${localize("_(Edited)By")} ${authorName}`}
							</span>
						</>
					)}
					{isNotNilOrWhiteSpace(categoryName) && (
						<>
							<PipeSeparator />
							<span {...setTrackingId("categoryName")}>
								{categoryName}
							</span>
						</>
					)}
				</ListItem.SubTitle>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
EventListItem.displayName = "EventListItem";